import type { ExecutionCandlesFetchParams } from "../services/tradeStatisticsApi";
import type { ExecutionCandlesResponse } from "../types/trade";
import { isExecutionCandlesBackfillIncomplete } from "./executionCandlesStatus";
import { executionCandlesCacheMatchesParams } from "./executionCandlesParams";

/** Align with BE execution-candles response cache (~1h). */
export const EXECUTION_CANDLES_RESPONSE_CACHE_TTL_MS = 60 * 60 * 1000;

type CacheEntry = {
  data: ExecutionCandlesResponse;
  expiresAt: number;
};

const responseCache = new Map<string, CacheEntry>();

export function isExecutionCandlesResponseCacheable(
  data: ExecutionCandlesResponse | null | undefined
): boolean {
  if (!data?.candles) return false;
  if (isExecutionCandlesBackfillIncomplete(data)) return false;
  if (data.backfill_complete === false) return false;
  return true;
}

export function readExecutionCandlesResponseCache(
  cacheKey: string,
  params: ExecutionCandlesFetchParams
): ExecutionCandlesResponse | null {
  const entry = responseCache.get(cacheKey);
  if (!entry) return null;
  if (Date.now() > entry.expiresAt) {
    responseCache.delete(cacheKey);
    return null;
  }
  if (!executionCandlesCacheMatchesParams(entry.data, params)) {
    responseCache.delete(cacheKey);
    return null;
  }
  if (!isExecutionCandlesResponseCacheable(entry.data)) {
    responseCache.delete(cacheKey);
    return null;
  }
  return entry.data;
}

export function writeExecutionCandlesResponseCache(
  cacheKey: string,
  data: ExecutionCandlesResponse
): void {
  if (!isExecutionCandlesResponseCacheable(data)) return;
  responseCache.set(cacheKey, {
    data,
    expiresAt: Date.now() + EXECUTION_CANDLES_RESPONSE_CACHE_TTL_MS,
  });
}

export function clearExecutionCandlesResponseCache(cacheKey?: string): void {
  if (cacheKey) {
    responseCache.delete(cacheKey);
    return;
  }
  responseCache.clear();
}
